import React, { useState } from 'react';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword, signInAnonymously, sendPasswordResetEmail } from 'firebase/auth';
import { doc, setDoc, Timestamp } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { Input, Button } from '../components/UI';

// --- Sub-komponen untuk Lupa Password ---
const ForgotPasswordForm = ({ onBack, showToast }) => {
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            await sendPasswordResetEmail(auth, email);
            if (showToast) showToast('Tautan reset password telah dikirim ke email Anda!', 'success');
            onBack();
        } catch (err) {
            setError(err.message.replace('Firebase: ', ''));
            if (showToast) showToast('Gagal mengirim email reset.', 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="w-full animate-fade-in-up">
            <button onClick={onBack} className="text-brand-purple font-semibold mb-6 flex items-center space-x-2 hover:text-purple-700 transition-colors">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
                <span>Kembali ke Login</span>
            </button>
            <div className="text-center mb-8">
                <div className="inline-block p-4 rounded-full bg-purple-100 text-purple-600 mb-4">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                    </svg>
                </div>
                <h1 className="text-3xl font-extrabold text-brand-dark mb-2">Lupa Password?</h1>
                <p className="text-brand-muted text-sm leading-relaxed">Masukkan email terdaftar Anda, dan kami akan mengirimkan tautan untuk mereset sandi Anda dengan aman.</p>
            </div>
            {error && <p className="bg-red-50 border-l-4 border-red-500 text-red-700 p-4 rounded-r-lg mb-6 text-sm shadow-sm">{error}</p>}
            <form onSubmit={handleSubmit} className="space-y-5">
                <Input id="reset-email" type="email" label="Email Terdaftar" value={email} onChange={(e) => setEmail(e.target.value)} required />
                <Button type="submit" disabled={loading} className="w-full shadow-lg shadow-purple-500/30">{loading ? 'Mengirim Tautan...' : 'Kirim Tautan Reset'}</Button>
            </form>
        </div>
    );
};

// --- Sub-komponen untuk Formulir Login/Register ---
const AuthForm = ({ isLogin, onBack, onForgotPassword, showToast }) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [username, setUsername] = useState('');
    const [parentDob, setParentDob] = useState('');
    const [domicile, setDomicile] = useState('');
    const [role, setRole] = useState('Orang Tua');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            if (isLogin) {
                await signInWithEmailAndPassword(auth, email, password);
            } else {
                const userCredential = await createUserWithEmailAndPassword(auth, email, password);
                await setDoc(doc(db, "users", userCredential.user.uid), {
                    email: userCredential.user.email,
                    username: username,
                    parentDob: Timestamp.fromDate(new Date(parentDob)),
                    domicile: domicile,
                    role: role,
                    createdAt: Timestamp.now()
                });
            }
        } catch (err) {
            setError(err.message.replace('Firebase: ', ''));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="w-full animate-fade-in-up">
            <button onClick={onBack} className="text-brand-purple font-semibold mb-6 flex items-center space-x-2 hover:text-purple-700 transition-colors">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
                <span>Kembali</span>
            </button>
            <div className="text-center mb-8">
                <h1 className="text-3xl font-extrabold text-brand-dark mb-1">{isLogin ? 'Selamat Datang Kembali!' : 'Buat Akun Baru'}</h1>
                <p className="text-brand-muted text-sm">{isLogin ? 'Silakan masuk ke akun si-kembang Anda.' : 'Mari mulai perjalanan bersama si-kembang.'}</p>
            </div>
            {error && <p className="bg-red-50 border-l-4 border-red-500 text-red-700 p-4 rounded-r-lg mb-6 text-sm shadow-sm">{error}</p>}
            <form onSubmit={handleSubmit} className="space-y-5">
                {!isLogin && (
                    <div className="space-y-5 animate-fade-in">
                        <Input id="username" type="text" label="Nama Lengkap Anda" value={username} onChange={(e) => setUsername(e.target.value)} required />
                        <Input id="parentDob" type="date" label="Tanggal Lahir Anda" value={parentDob} onChange={(e) => setParentDob(e.target.value)} required />
                        <Input id="domicile" type="text" label="Domisili (Kota/Kabupaten)" value={domicile} onChange={(e) => setDomicile(e.target.value)} required />
                    </div>
                )}
                <Input id="email" type="email" label="Email" value={email} onChange={(e) => setEmail(e.target.value)} required />
                <div>
                    <Input id="password" type="password" label="Password (min. 6 karakter)" value={password} onChange={(e) => setPassword(e.target.value)} required />
                    {isLogin && (
                        <div className="text-right mt-2">
                            <button type="button" onClick={onForgotPassword} className="text-sm font-semibold text-brand-purple hover:text-pink-600 transition-colors">
                                Lupa Password?
                            </button>
                        </div>
                    )}
                </div>
                {!isLogin && (
                    <div className="animate-fade-in">
                        <label htmlFor="role" className="block text-gray-700 text-sm font-bold mb-2">Saya mendaftar sebagai</label>
                        <select id="role" value={role} onChange={e => setRole(e.target.value)} className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-purple focus:border-transparent transition" required>
                            <option value="Orang Tua">Orang Tua</option>
                            <option value="Tenaga Kesehatan">Tenaga Kesehatan</option>
                            <option value="Guru PAUD">Guru PAUD</option>
                        </select>
                    </div>
                )}
                <Button type="submit" disabled={loading} className="w-full shadow-lg shadow-purple-500/30 mt-4">{loading ? 'Memproses...' : (isLogin ? 'Masuk Sekarang' : 'Daftar Akun')}</Button>
            </form>
        </div>
    );
};

// --- Komponen Utama Layar Otentikasi ---
export default function AuthScreen({ showToast }) {
    const [authAction, setAuthAction] = useState(null); // 'login' or 'register' or 'forgotPassword'

    const handleGuestLogin = async () => {
        try {
            await signInAnonymously(auth);
            if (showToast) showToast("Anda masuk sebagai Tamu.", "info");
        } catch (error) {
            console.error("Anonymous login failed", error);
            if (showToast) showToast("Gagal masuk sebagai Tamu.", "error");
        }
    };

    const ActionButton = ({ title, description, icon, colorClass, onClick }) => (
        <button onClick={onClick} className={`text-left w-full p-5 rounded-2xl border-2 border-transparent hover:border-${colorClass}-300 bg-white shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group`}>
            <div className="flex items-center space-x-4">
                <div className={`p-3 rounded-xl bg-${colorClass}-50 text-${colorClass}-500 group-hover:scale-110 transition-transform`}>
                    {icon}
                </div>
                <div className="flex-grow">
                    <h3 className="font-bold text-gray-800 text-lg">{title}</h3>
                    <p className="text-sm text-gray-500">{description}</p>
                </div>
                <span className={`text-${colorClass}-400 text-2xl transform group-hover:translate-x-1 transition-transform`}>&rarr;</span>
            </div>
        </button>
    );

    return (
        <div className="min-h-screen flex flex-col justify-center items-center p-6 relative overflow-hidden bg-gradient-to-br from-teal-50 via-sky-50 to-purple-50">
            {/* Animated Background Blobs */}
            <div className="absolute top-0 left-0 w-64 h-64 bg-purple-300 rounded-full mix-blend-multiply filter blur-3xl opacity-40 animate-blob pointer-events-none"></div>
            <div className="absolute top-0 right-0 w-64 h-64 bg-teal-300 rounded-full mix-blend-multiply filter blur-3xl opacity-40 animate-blob animation-delay-2000 pointer-events-none"></div>
            <div className="absolute -bottom-8 left-20 w-64 h-64 bg-pink-300 rounded-full mix-blend-multiply filter blur-3xl opacity-40 animate-blob animation-delay-4000 pointer-events-none"></div>

            <div className="relative z-10 w-full max-w-md">
                <div className="bg-white/80 backdrop-blur-xl rounded-[2.5rem] shadow-[0_8px_30px_rgb(0,0,0,0.08)] border border-white/60 p-8 sm:p-10 transition-all duration-500">
                    {authAction === 'forgotPassword' ? (
                        <ForgotPasswordForm onBack={() => setAuthAction('login')} showToast={showToast} />
                    ) : authAction ? (
                        <AuthForm isLogin={authAction === 'login'} onBack={() => setAuthAction(null)} onForgotPassword={() => setAuthAction('forgotPassword')} showToast={showToast} />
                    ) : (
                        <div className="animate-fade-in-up w-full flex flex-col items-center">
                            <div className="mb-8 flex flex-col items-center">
                                {/* New Logo Implementation */}
                                <div className="w-32 h-32 rounded-[2rem] bg-white p-2 shadow-lg shadow-teal-500/20 mb-6 transform hover:rotate-3 transition-transform duration-500">
                                    <img src="/assets/images/logo_sikembang.png" alt="Si-Kembang Logo" className="w-full h-full object-cover rounded-3xl" />
                                </div>
                                <h1 className="text-3xl sm:text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-teal-600 to-purple-600 mb-2">si-kembang</h1>
                                <p className="text-gray-500 text-center font-medium">Sistem Informasi Kesehatan Tumbuh Kembang</p>
                            </div>

                            <div className="space-y-4 w-full">
                                <ActionButton 
                                    title="Masuk Akun"
                                    description="Sudah punya akun? Masuk di sini."
                                    colorClass="purple"
                                    icon={<svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" /></svg>}
                                    onClick={() => setAuthAction('login')}
                                />
                                <ActionButton 
                                    title="Daftar Baru"
                                    description="Bergabung sebagai Orang Tua atau Nakes."
                                    colorClass="teal"
                                    icon={<svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" /></svg>}
                                    onClick={() => setAuthAction('register')}
                                />
                                <ActionButton 
                                    title="Coba sebagai Tamu"
                                    description="Jelajahi fitur tanpa perlu mendaftar."
                                    colorClass="sky"
                                    icon={<svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>}
                                    onClick={handleGuestLogin}
                                />
                            </div>
                        </div>
                    )}
                </div>
                
                {/* Footer Requirement */}
                <div className="relative z-10 mt-8 text-center px-4 animate-fade-in">
                    <p className="text-xs sm:text-sm text-gray-500 leading-relaxed font-medium">
                        Aplikasi ini dikembangkan dan didanai oleh <br/>
                        <span className="font-bold text-gray-700">Hibah Kemenristekdikti tahun 2025</span>
                    </p>
                </div>
            </div>

            <style>{`
                @keyframes fade-in-up {
                    0% { opacity: 0; transform: translateY(20px); }
                    100% { opacity: 1; transform: translateY(0); }
                }
                .animate-fade-in-up {
                    animation: fade-in-up 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards;
                }
                @keyframes fade-in {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                .animate-fade-in {
                    animation: fade-in 0.8s ease-out forwards;
                }
                @keyframes blob {
                    0% { transform: translate(0px, 0px) scale(1); }
                    33% { transform: translate(30px, -50px) scale(1.1); }
                    66% { transform: translate(-20px, 20px) scale(0.9); }
                    100% { transform: translate(0px, 0px) scale(1); }
                }
                .animate-blob {
                    animation: blob 7s infinite;
                }
                .animation-delay-2000 {
                    animation-delay: 2s;
                }
                .animation-delay-4000 {
                    animation-delay: 4s;
                }
            `}</style>
        </div>
    );
}
